import { useState, useRef, useEffect } from 'react';
import { X, Send, Sparkles, Loader2 } from 'lucide-react';
import {
  apiClient,
  resolveAiUserId,
  getOrCreateAiSessionId,
} from '@/services/api-client';
import { resolveWorkspaceSessionId } from '@/services/workspaceSession';
import { useAuthStore } from '@/stores/authStore';

interface AIAssistantProps {
  onClose: () => void;
}

const WELCOME_MESSAGE =
  "Hello! I'm your Khiimori AI assistant. I can help you organize tabs, generate applications, and answer questions about your browsing session.";

export function AIAssistant({ onClose }: AIAssistantProps) {
  const user = useAuthStore((state) => state.user);
  const userId = resolveAiUserId(user?.id);

  const [messages, setMessages] = useState<Array<{ role: 'user' | 'assistant'; content: string }>>([
    { role: 'assistant', content: WELCOME_MESSAGE },
  ]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const ensureConversation = async (): Promise<string> => {
    if (conversationId) return conversationId;
    const sessionId = (await resolveWorkspaceSessionId()) ?? getOrCreateAiSessionId();
    const row = await apiClient.createAiConversation({
      userId,
      sessionId,
      type: 'tab_context',
    });
    const id = String((row as { id: string }).id);
    setConversationId(id);
    return id;
  };

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);
    setError(null);

    try {
      const cid = await ensureConversation();
      const result = await apiClient.respondAiConversation(cid, userMessage);
      const assistantContent = result.response || 'No response received';
      setMessages((prev) => [...prev, { role: 'assistant', content: assistantContent }]);
    } catch (err) {
      console.error('AI assistant error:', err);
      setError('Failed to get a response. Please try again.');
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content:
            "Sorry, I couldn't process your request right now. Please check that the backend is running and try again.",
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 modal-backdrop z-50 flex items-center justify-center p-4">
      <div className="glass-card rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-khiimori-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-khiimori-text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-khiimori-text-primary">AI Assistant</h2>
              <p className="text-sm text-khiimori-text-muted">Powered by multi-provider AI</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-khiimori-bg-elevated rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-khiimori-text-muted" />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                  msg.role === 'user'
                    ? 'bg-indigo-500 text-white'
                    : 'bg-khiimori-bg-elevated text-khiimori-text-primary border border-khiimori-border/40'
                }`}
              >
                <p className={`text-sm leading-relaxed ${msg.role === 'user' ? 'text-white' : 'text-khiimori-text-primary'}`}>
                  {msg.content}
                </p>
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-khiimori-bg-elevated rounded-2xl px-4 py-3">
                <Loader2 className="w-5 h-5 text-khiimori-text-muted animate-spin" />
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-4 border-t border-khiimori-border">
          {error && (
            <p className="text-xs text-red-400 mb-2">{error}</p>
          )}
          <div className="flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSend()}
              placeholder="Ask about your tabs, or request an app to be generated..."
              className="flex-1 px-4 py-3 rounded-xl border border-khiimori-border bg-khiimori-bg-elevated text-khiimori-text-primary placeholder:text-khiimori-text-muted text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-500"
            />
            <button
              onClick={handleSend}
              disabled={!input.trim() || isLoading}
              className="px-6 py-3 bg-indigo-500 text-white rounded-xl hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}